import { formatISODate } from '@common/utils/dateUtils';
import { ActionButton } from '@common/components';
import {
  EditIcon,
  ViewIcon,
  PublishIcon,
  UnpublishIcon,
  Delete2Icon,
} from '@common/components/icons';
import { ActionsCell } from '@common/components/Table/Cells';
import EditableStatusDropdown, { STATUS_TYPES } from '@common/components/EditableStatusDropdown/EditableStatusDropdown';
import * as styles from './styles.module.css';

// System default reports (seeded predefined reports) are read-only:
// they cannot be edited, renamed or deleted.
const isDefaultReport = (row) => Boolean(row?.original?.isDefault);

export const createColumns = (
  t,
  { onView, onEdit, onPublish, onUnpublish, onDelete, onUpdateStatus, isAdmin = false },
) => {
  const actionsMenu = [
    {
      key: 'view',
      component: (
        <ActionButton
          icon={ViewIcon}
          text={t('reports.actions.view')}
          className={styles['action-button']}
        />
      ),
      onClick: (row) => {
        onView?.(row.original);
      },
    },
    {
      key: 'edit',
      component: (row) => (
        <ActionButton
          icon={EditIcon}
          text={t('reports.actions.edit')}
          className={styles['action-button']}
          disabled={isDefaultReport(row)}
          title={isDefaultReport(row) ? t('reports.actions.defaultReadOnly') : undefined}
        />
      ),
      disabled: isDefaultReport,
      onClick: (row) => {
        onEdit?.(row.original);
      },
    },
    {
      key: 'publish',
      component: (row) => (
        <ActionButton
          icon={PublishIcon}
          text={t('reports.actions.publish')}
          className={styles['action-button']}
          disabled={isDefaultReport(row)}
          title={isDefaultReport(row) ? t('reports.actions.defaultReadOnly') : undefined}
        />
      ),
      disabled: isDefaultReport,
      onClick: (row) => {
        onPublish?.(row.original);
      },
      hidden: (row) => row.original.status !== 0, // Hide if not draft
    },
    {
      key: 'unpublish',
      component: (row) => (
        <ActionButton
          icon={UnpublishIcon}
          text={t('reports.actions.unpublish')}
          className={styles['action-button']}
          disabled={isDefaultReport(row)}
          title={isDefaultReport(row) ? t('reports.actions.defaultReadOnly') : undefined}
        />
      ),
      disabled: isDefaultReport,
      onClick: (row) => {
        onUnpublish?.(row.original);
      },
      hidden: (row) => row.original.status !== 1, // Hide if not published
    },
    {
      key: 'delete',
      component: (row) => (
        <ActionButton
          icon={Delete2Icon}
          text={t('reports.actions.delete')}
          className={`${styles['action-button']} ${styles['delete-action']}`}
          disabled={isDefaultReport(row)}
          title={isDefaultReport(row) ? t('reports.actions.defaultReadOnly') : undefined}
        />
      ),
      disabled: isDefaultReport,
      onClick: (row) => {
        onDelete?.(row.original);
      },
    },
  ];

  return [
    {
      id: 'select',
      staticWidth: 37,
      enableResizing: false,
      enableSorting: false,
      meta: { label: 'Selection' },
    },
    {
      accessorKey: 'name',
      header: t('common.name'),
      enableColumnFilter: true,
      enableColumnResizing: true,
      minWidth: 120,
      cell: ({ getValue }) => getValue() || '-',
    },
    {
      accessorKey: 'description',
      header: t('common.description'),
      enableColumnFilter: true,
      enableColumnResizing: true,
      minWidth: 200,
      cell: ({ getValue }) => getValue() || '-',
    },
    // Only include status column if user has admin privileges
    ...(isAdmin ? [{
      accessorKey: 'status',
      header: t('reports.columns.status'),
      enableColumnFilter: true,
      enableColumnResizing: false,
      staticWidth: 130,
      cell: ({ getValue, row }) => {
        const status = getValue();
        const report = row.original;
        return (
          <EditableStatusDropdown
            value={status}
            onUpdate={(newValue) => onUpdateStatus?.(report, newValue)}
            statusType={STATUS_TYPES.PUBLISHED_DRAFT}
            showStatusDot
            disabled={!isAdmin || Boolean(report.isDefault)}
          />
        );
      },
    }] : []),
    {
      accessorKey: 'updatedAt',
      header: t('reports.columns.lastUpdated'),
      enableColumnFilter: true,
      enableColumnResizing: true,
      minWidth: 150,
      cell: ({ getValue }) => formatISODate(getValue()),
    },
    {
      accessorKey: 'creator',
      header: t('reports.columns.creator'),
      enableColumnFilter: true,
      enableColumnResizing: true,
      minWidth: 120,
      cell: ({ getValue }) => getValue() || '-',
    },
    // Only include actions column if user has admin privileges
    ...(isAdmin ? [{
      id: 'actions',
      staticWidth: 27,
      cell: ({ row }) => <ActionsCell row={row} actionsMenu={actionsMenu} />,
      enableResizing: false,
      enableSorting: false,
      meta: { label: 'Actions' },
    }] : []),
  ];
};
